/**
 * CL-PRODUCT-SELECTIONS-006A — Product option ordering (pure).
 */

import {
  PRODUCT_OPTION_RECORD_LIFECYCLE,
  deriveProductOptionLifecycleAndDecision,
} from "./projectProductOptionDecisionModel";

const DEFAULT_SORT_ORDER = 1000;
const SORT_ORDER_STEP = 10;

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeSortOrder(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.trunc(parsed);
}

export function sortProductOptionsByOrder(options = [], { approvedOptionId = "" } = {}) {
  return [...(Array.isArray(options) ? options : [])].sort((left, right) => {
    const leftOrder = normalizeSortOrder(left?.sortOrder, DEFAULT_SORT_ORDER);
    const rightOrder = normalizeSortOrder(right?.sortOrder, DEFAULT_SORT_ORDER);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return safeStr(left?.label).localeCompare(safeStr(right?.label));
  });
}

export function resolveNextProductOptionSortOrder(options = [], fallback = DEFAULT_SORT_ORDER) {
  const values = (Array.isArray(options) ? options : [])
    .map((row) => normalizeSortOrder(row?.sortOrder, NaN))
    .filter((value) => Number.isFinite(value));
  if (!values.length) return fallback;
  return Math.max(...values) + SORT_ORDER_STEP;
}

function workingListOptions(options = [], { approvedOptionId = "" } = {}) {
  return sortProductOptionsByOrder(options, { approvedOptionId }).filter((row) => {
    const derived = deriveProductOptionLifecycleAndDecision(row, { approvedOptionId });
    return derived.recordLifecycle !== PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED;
  });
}

export function computeProductOptionReorderUpdates(options = [], optionId = "", direction = "up", {
  approvedOptionId = "",
} = {}) {
  const sorted = workingListOptions(options, { approvedOptionId });
  const index = sorted.findIndex((row) => safeStr(row.id) === safeStr(optionId));
  if (index < 0) return { ok: false, error: "Product not found." };
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= sorted.length) {
    return {
      ok: false,
      error: direction === "up" ? "Product is already first." : "Product is already last.",
    };
  }
  const current = sorted[index];
  const neighbor = sorted[targetIndex];
  const currentOrder = normalizeSortOrder(current.sortOrder, (index + 1) * SORT_ORDER_STEP);
  const neighborOrder = normalizeSortOrder(neighbor.sortOrder, (targetIndex + 1) * SORT_ORDER_STEP);
  return {
    ok: true,
    updates: [
      { id: current.id, sortOrder: neighborOrder },
      { id: neighbor.id, sortOrder: currentOrder },
    ],
  };
}

export function buildProductOptionOrderFingerprintInput(options = [], { approvedOptionId = "" } = {}) {
  return sortProductOptionsByOrder(options, { approvedOptionId }).map((row) => ({
    optionId: safeStr(row.id),
    sortOrder: normalizeSortOrder(row.sortOrder, DEFAULT_SORT_ORDER),
  }));
}

export function productOptionOrderMatchesPublished(canonicalOptions = [], publishedOptions = [], {
  approvedOptionId = "",
} = {}) {
  const canonical = buildProductOptionOrderFingerprintInput(canonicalOptions, { approvedOptionId });
  const published = (Array.isArray(publishedOptions) ? publishedOptions : [])
    .filter((row) => safeStr(row.status) === "active")
    .sort((left, right) => normalizeSortOrder(left.sortOrder) - normalizeSortOrder(right.sortOrder))
    .map((row) => ({
      optionId: safeStr(row.optionId) || safeStr(row.id),
      sortOrder: normalizeSortOrder(row.sortOrder, DEFAULT_SORT_ORDER),
    }));

  if (canonical.length !== published.length) return false;
  return canonical.every((row, index) => (
    row.optionId === published[index]?.optionId
    && row.sortOrder === published[index]?.sortOrder
  ));
}
